import { Component, Input, signal } from '@angular/core';
import {
    FilterOption,
    IEntity,
    ISearchFacade,
    SafeAny,
    SelectOption,
} from '@pf/shared-common';
import { FormControl } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BaseFilterComponent } from './base-filter.component';
import { combineLatest, take } from 'rxjs';
import { FilterMetadata } from '../stores/filter.store';
import { NzCheckboxOption } from 'ng-zorro-antd/checkbox';

type CheckboxGroupFilterOption = FilterOption &
    NzCheckboxOption & {
        customFilterKey: string;
    };

interface MultiEntityFilterProps {
    filterKey: string;
    value: string[];
}

@UntilDestroy()
@Component({
    selector: 'pf-checkbox-group-filter',
    templateUrl: './checkbox-group-filter.component.html',
    styleUrl: './checkbox-group-filter.component.scss',
    standalone: false,
})
export class CheckboxGroupFilterComponent extends BaseFilterComponent {
    selectOptions = signal<CheckboxGroupFilterOption[]>([]);
    selectEntityOptions: Record<
        string,
        Array<CheckboxGroupFilterOption & { checked: boolean }>
    > = {};
    selectedEntityValues: Record<string, Array<string | number>> = {};
    searchControl = new FormControl<string>('');
    indeterminate = false;

    selected: Array<string | number> = [];

    @Input() allChecked = false;

    isEntityOptionsAvailable(key: string): boolean {
        return !!key && this.selectEntityOptions?.[key]?.length > 0;
    }

    updateAllChecked(): void {
        this.indeterminate = false;
        this.updateAllOptions(this.allChecked);

        this.filterChangedChecks();
    }

    updateSingleChecked(): void {
        this.checkAllOptionsAreChecked();
        this.filterChangedChecks();
    }

    updateGroupChecked(
        entityType: string,
        selectedValues: Array<string | number>
    ): void {
        this.selectedEntityValues[entityType] = selectedValues ?? [];
        this.syncGroupCheckedState(entityType);
        this.syncSelectedFromGroupedValues();
        this.checkAllOptionsAreChecked();
        this.filterChangedChecks();
    }

    selectItem(option: SelectOption) {
        if (option.group) {
            this.selectEntityItem(option);
            return;
        }

        if (this.selected.includes(option.value)) {
            return;
        }

        this.selectOptions.set([
            {
                label: option.label,
                value: option.value,
            } as CheckboxGroupFilterOption,
            ...this.selectOptions(),
        ]);
        this.addSelected(option.value);

        this.checkAllOptionsAreChecked();
        this.filterChangedChecks();
    }

    entityGroupDelete(entityType: string) {
        delete this.selectEntityOptions[entityType];
        delete this.selectedEntityValues[entityType];
        this.selectOptions.set(Object.values(this.selectEntityOptions).flat());
        this.syncSelectedFromGroupedValues();
        this.checkAllOptionsAreChecked();
        this.filterChangedChecks();
    }

    override filterChangedChecks() {
        this.filterChangeHandler({
            key: this.filterKey,
            name: this.filterName,
            value: this.getSelectedValuesMetadata(),
            props: this.isGrouped
                ? this.getSelectedEntitiesMetadata()
                : undefined,
        });
        this.validate();
    }

    override validate() {
        const isAnyOptionChecked = this.selected.length > 0;

        this.displayValidationMessage =
            !isAnyOptionChecked && this.selectionRequired;
    }

    override setFilterOptions(options: FilterOption[] | undefined) {
        if (!options) return;

        this.selectOptions.set(this.mapToCheckboxGroupFilterOptions(options));
    }

    override handleInitialValue() {
        combineLatest([
            this.filterService.checkInitialFilterIsDefinedByName$(
                this.filterName
            ),
            this.filterService.getAppliedFilterByName$(this.filterName),
        ])
            .pipe(take(1))
            .subscribe(([initialFiltersDefined, filter]) => {
                if (filter) {
                    this.transformFilterMetadataToFilterOptions(filter);
                }

                if (!initialFiltersDefined && this.initialSelection === 'all') {
                    this.allChecked = true;
                    this.updateAllChecked();
                    this.updateInitialFilter({});
                }

                if (initialFiltersDefined) {
                    this.updateInitialFilter(filter);
                }

                super.handleInitialValue();
            });
    }

    override updateInitialFilter(_: SafeAny) {
        const filter = {
            key: this.filterKey,
            name: this.filterName,
            value: this.selectOptions()
                .filter(option => option.initial)
                .map(option => option.value.toString()),
        } as FilterMetadata;
        this.filterService.setInitialFilter(filter);
    }

    override filterResetFn() {
        this.resetOrClearOptions(true);
    }

    override filterClearFn() {
        this.resetOrClearOptions(false);
    }

    private clearAllOptions() {
        this.selectOptions.set([]);
        this.selectEntityOptions = {};
        this.selectedEntityValues = {};
    }

    private updateAllOptions(checked: boolean) {
        if (!checked) {
            this.selected = this.selectOptions()
                .filter(option => option.initial)
                .map(item => item.value);
        } else {
            this.selected = this.selectOptions().map(item => item.value);
        }

        for (const key in this.selectEntityOptions) {
            this.selectEntityOptions[key] = this.selectEntityOptions[key].map(
                item => ({
                    ...item,
                    checked: checked,
                })
            );
            this.selectedEntityValues[key] = checked
                ? this.selectEntityOptions[key].map(item => item.value)
                : [];
        }
    }

    private checkAllOptionsAreChecked() {
        const isAllChecked =
            this.selected.length === this.selectOptions().length;
        const isAnyOptionChecked = this.selected.length > 0;

        this.allChecked = isAllChecked;
        this.indeterminate = isAnyOptionChecked && !isAllChecked;
    }

    private cleanUnselectedOptionsFromFilters() {
        const unSelectedFilters = this.selectOptions().filter(
            filter => !this.selected.includes(filter.value)
        );

        for (const unselectedFilter of unSelectedFilters) {
            this.filterService.deleteFilter(
                unselectedFilter.customFilterKey || this.filterKey
            );
        }
    }

    private fetchOptionsForFacade(ids: string[]) {
        if (!ids || ids.length === 0) return;

        (this.searchFacade as ISearchFacade<IEntity>)
            .getMany$(ids)
            .subscribe(entities => {
                this.selectOptions.set(
                    entities.map(entity => ({
                        label: (
                            this.searchFacade as ISearchFacade<IEntity>
                        ).getEntityName(entity),
                        value: entity.id,
                        customFilterKey: this.filterKey,
                    }))
                );
                this.selected = entities.map(entity => entity.id);
            });
    }

    private fetchOptionsForEntityTypes(metadata: FilterMetadata) {
        for (const key in this.searchEntityTypes) {
            const entityIds = (metadata.props as MultiEntityFilterProps[]).find(
                prop =>
                    prop.filterKey === this.searchEntityTypes?.[key].filterKey
            )?.value;

            if (entityIds && entityIds?.length > 0) {
                (
                    this.searchEntityTypes[key]
                        .searchFacade as ISearchFacade<IEntity>
                )
                    ?.getMany$(entityIds as string[])
                    .subscribe(entities => {
                        entities.forEach(entity => {
                            this.selectEntityItem({
                                label: (entity as SafeAny)[
                                    this.searchEntityTypes?.[key]
                                        .searchFacadeLabel ?? ''
                                ],
                                value: entity.id ?? '',
                                group: key,
                            });
                        });
                    });
            }
        }
    }

    private selectEntityItem(
        selection: SelectOption,
        filterChangeCheck = true
    ) {
        const entityType = selection.group;

        if (!entityType) {
            console.error('Entity type is not provided');
            return;
        }

        if (!this.selectEntityOptions[entityType]) {
            this.selectEntityOptions[entityType] = [];
        }

        const isOptionExists = this.selectEntityOptions[entityType].some(
            item => item.value === selection.value
        );

        if (isOptionExists) return;

        const customFilterKey = this.searchEntityTypes?.[entityType]?.filterKey;

        this.selectEntityOptions[entityType] = [
            ...this.selectEntityOptions[entityType],
            {
                label: selection.label ?? selection.value,
                value: selection.value,
                checked: true,
                customFilterKey: customFilterKey ?? this.filterKey,
            },
        ];

        if (!this.selectedEntityValues[entityType]) {
            this.selectedEntityValues[entityType] = [];
        }
        if (!this.selectedEntityValues[entityType].includes(selection.value)) {
            this.selectedEntityValues[entityType] = [
                ...this.selectedEntityValues[entityType],
                selection.value,
            ];
        }

        this.addSelected(selection.value);

        this.updateSelectOptions(filterChangeCheck);
    }

    private mapToCheckboxGroupFilterOptions(
        options: FilterOption[]
    ): CheckboxGroupFilterOption[] {
        return options.map(o => {
            return {
                label: o.label,
                value: o.value,
                initial: o.initial,
                customFilterKey: this.filterKey,
            } as CheckboxGroupFilterOption;
        });
    }

    private updateSelectOptions(filterChangeCheck = true) {
        this.selectOptions.set(Object.values(this.selectEntityOptions).flat());
        this.syncSelectedFromGroupedValues();
        this.checkAllOptionsAreChecked();
        if (filterChangeCheck) {
            this.filterChangedChecks();
        }
    }

    private syncSelectedFromGroupedValues() {
        const checkedValues: Array<string | number> = [];
        for (const key in this.selectedEntityValues) {
            for (const value of this.selectedEntityValues[key] ?? []) {
                checkedValues.push(value);
            }
        }
        this.selected = checkedValues;
    }

    private syncGroupCheckedState(entityType: string) {
        const selectedValues = this.selectedEntityValues[entityType] ?? [];
        const options = this.selectEntityOptions[entityType];
        if (!options) return;

        this.selectEntityOptions[entityType] = options.map(option => ({
            ...option,
            checked: selectedValues.includes(option.value),
        }));
    }

    private resetOrClearOptionsNoSearchFacade(resetAll: boolean) {
        this.indeterminate = false;
        this.allChecked = !resetAll && this.initialSelection === 'all';
        this.updateAllOptions(this.allChecked);
        this.filterChangedChecks();
    }

    private resetOrClearOptions(resetAll: boolean) {
        if (!this.searchFacade) {
            this.resetOrClearOptionsNoSearchFacade(resetAll);
            return;
        }

        this.allChecked = false;
        this.indeterminate = false;

        if (resetAll) {
            this.clearAllOptions();
        } else {
            this.updateAllOptions(false);
        }

        this.validate();
    }

    private getSelectedValuesMetadata() {
        this.cleanUnselectedOptionsFromFilters();
        return this.selected.map(s => s.toString());
    }

    private getSelectedEntitiesMetadata() {
        const selectedEntities: MultiEntityFilterProps[] = [];
        for (const key in this.selectEntityOptions) {
            const selectedFilters = this.selectEntityOptions[key].filter(
                filter => this.selected.includes(filter.value)
            );

            selectedEntities.push({
                filterKey: this.searchEntityTypes?.[key]?.filterKey ?? '',
                value: selectedFilters.map(filter => filter.value.toString()),
            });
        }

        return selectedEntities;
    }

    private transformFilterMetadataToFilterOptions(
        metadata: FilterMetadata
    ): void {
        if (!metadata?.value) return;

        if (metadata.value && !this.searchFacade && !this.searchEntityTypes) {
            this.selectOptions().forEach(option => {
                if (
                    (metadata.value as string[]).includes(
                        option.value.toString()
                    )
                ) {
                    this.addSelected(option.value);
                } else {
                    this.removeSelected(option.value);
                }
            });
        }

        if (this.searchEntityTypes) {
            this.fetchOptionsForEntityTypes(metadata);
        }

        if (!this.searchEntityTypes && this.searchFacade) {
            this.fetchOptionsForFacade(metadata.value as string[]);
        }

        this.checkAllOptionsAreChecked();
    }

    private addSelected(value: string | number) {
        if (this.selected.includes(value)) return;
        this.selected.push(value);
    }

    private removeSelected(value: string | number) {
        this.selected = this.selected.filter(item => item !== value);
    }
}
